import type { ConfigFotovoltaicoDTO } from '@/models/dto/fotovoltaico/config-fotovoltaico-dto'
import type { MetadataFotovoltaicoDTO } from '@/models/dto/fotovoltaico/metadata-fotovoltaico-dto'
import type { PhotovoltaicPropertyData } from '@/models/types/photovoltaic/photovoltaic-property-data'
import type { PhotovoltaicSettings } from '@/models/types/photovoltaic/photovoltaic-settings'
import { mapToMetadataFotovoltaicoDTO, mapToPhotovoltaicPropertyData } from './photovoltaic-property-data-mapper'

function mapToConfigFotovoltaicoDTO(photovoltaicSettings: PhotovoltaicSettings): ConfigFotovoltaicoDTO {
    const metadataFotovoltaico: MetadataFotovoltaicoDTO[] = []

    if (photovoltaicSettings.data) {
        for (const inputValue of Object.values(photovoltaicSettings.data)) {
            if (!inputValue) continue
            metadataFotovoltaico.push(mapToMetadataFotovoltaicoDTO(inputValue))
        }
    }

    const configFotovoltaicoDTO: ConfigFotovoltaicoDTO = {
        id: photovoltaicSettings.id,
        metadataFotovoltaico,
        fondoScalaMinCo2: photovoltaicSettings.co2Min,
        fondoScalaMaxCo2: photovoltaicSettings.co2Max,
        fondoScalaMinProd: photovoltaicSettings.productionMin,
        fondoScalaMaxProd: photovoltaicSettings.productionMax,
        fondoScalaMinCons: photovoltaicSettings.consumptionMin,
        fondoScalaMaxCons: photovoltaicSettings.consumptionMax,
        abilitaCo2: photovoltaicSettings.co2Enabled,
        abilitaProduzione: photovoltaicSettings.productionEnabled,
        ablitiaConsumi: photovoltaicSettings.consumptionEnabled,
        uoMCo2: photovoltaicSettings.co2Uom ?? null,
        uoMProd: photovoltaicSettings.productionUom ?? null,
        uoMCons: photovoltaicSettings.consumptionUom ?? null,

        sogliaMinima: photovoltaicSettings.sogliaMinima ?? null,
        tempoInattivita: photovoltaicSettings.tempoInattivita ?? null,
        notificaAutomatica: photovoltaicSettings.notificaAutomatica ?? null,
        fasciaOrariaDa: photovoltaicSettings.fasciaOrariaDa ?? null,
        fasciaOrariaA: photovoltaicSettings.fasciaOrariaA ?? null,
        //productionNotificationEmail: photovoltaicSettings.productionNotificationEmail ?? null,

        gennaio: photovoltaicSettings.gennaio ?? null,
        febbraio: photovoltaicSettings.febbraio ?? null,
        marzo: photovoltaicSettings.marzo ?? null,
        aprile: photovoltaicSettings.aprile ?? null,
        maggio: photovoltaicSettings.maggio ?? null,
        giugno: photovoltaicSettings.giugno ?? null,
        luglio: photovoltaicSettings.luglio ?? null,
        agosto: photovoltaicSettings.agosto ?? null,
        settembre: photovoltaicSettings.settembre ?? null,
        ottobre: photovoltaicSettings.ottobre ?? null,
        novembre: photovoltaicSettings.novembre ?? null,
        dicembre: photovoltaicSettings.dicembre ?? null,
    }

    return configFotovoltaicoDTO
}

function mapToPhotovoltaicSettings(configFotovoltaicoDTO: ConfigFotovoltaicoDTO): PhotovoltaicSettings {
    const data: Record<string, PhotovoltaicPropertyData> = {}

    if (configFotovoltaicoDTO.metadataFotovoltaico) {
        for (const metadata of configFotovoltaicoDTO.metadataFotovoltaico) {
            if (!metadata.key) continue
            data[metadata.key] = mapToPhotovoltaicPropertyData(metadata)
        }
    }

    const photovoltaicSettings: PhotovoltaicSettings = {
        id: configFotovoltaicoDTO.id,
        data,
        co2Min: configFotovoltaicoDTO.fondoScalaMinCo2,
        co2Max: configFotovoltaicoDTO.fondoScalaMaxCo2,
        productionMin: configFotovoltaicoDTO.fondoScalaMinProd,
        productionMax: configFotovoltaicoDTO.fondoScalaMaxProd,
        consumptionMin: configFotovoltaicoDTO.fondoScalaMinCons,
        consumptionMax: configFotovoltaicoDTO.fondoScalaMaxCons,
        co2Enabled: configFotovoltaicoDTO.abilitaCo2,
        productionEnabled: configFotovoltaicoDTO.abilitaProduzione,
        consumptionEnabled: configFotovoltaicoDTO.ablitiaConsumi,
        co2Uom: configFotovoltaicoDTO.uoMCo2 ?? undefined,
        productionUom: configFotovoltaicoDTO.uoMProd ?? undefined,
        consumptionUom: configFotovoltaicoDTO.uoMCons ?? undefined,

        sogliaMinima: configFotovoltaicoDTO.sogliaMinima ?? undefined,
        tempoInattivita: configFotovoltaicoDTO.tempoInattivita ?? undefined,
        notificaAutomatica: configFotovoltaicoDTO.notificaAutomatica ?? undefined,
        fasciaOrariaDa: configFotovoltaicoDTO.fasciaOrariaDa ?? undefined,
        fasciaOrariaA: configFotovoltaicoDTO.fasciaOrariaA ?? undefined,
        //productionNotificationEmail: configFotovoltaicoDTO.productionNotificationEmail ?? undefined,

        gennaio: configFotovoltaicoDTO.gennaio ?? undefined,
        febbraio: configFotovoltaicoDTO.febbraio ?? undefined,
        marzo: configFotovoltaicoDTO.marzo ?? undefined,
        aprile: configFotovoltaicoDTO.aprile ?? undefined,
        maggio: configFotovoltaicoDTO.maggio ?? undefined,
        giugno: configFotovoltaicoDTO.giugno ?? undefined,
        luglio: configFotovoltaicoDTO.luglio ?? undefined,
        agosto: configFotovoltaicoDTO.agosto ?? undefined,
        settembre: configFotovoltaicoDTO.settembre ?? undefined,
        ottobre: configFotovoltaicoDTO.ottobre ?? undefined,
        novembre: configFotovoltaicoDTO.novembre ?? undefined,
        dicembre: configFotovoltaicoDTO.dicembre ?? undefined,
    }

    return photovoltaicSettings
}

export { mapToConfigFotovoltaicoDTO, mapToPhotovoltaicSettings }
